/**
 * Low-level WebSocket connection wrapper for F-300's `/ws/mirlai/v1`
 * (T-008).
 *
 * Owns:
 *   - Native `WebSocket` lifecycle (open / message / close / error)
 *   - Auth handshake: send `{ type: "auth", token }` on open, wait for
 *     `auth_ok` or `auth_error` from the server
 *   - Topic subscription: send `{ type: "subscribe", topics }` after
 *     `auth_ok` (defaults to `["devices", "suggestions"]`)
 *   - Exponential-backoff reconnect with ±20% jitter, capped at 30 s
 *   - Token resolution at **send time** (via injected `getToken`) so
 *     mid-session token rotation works without rebuilding the class
 *   - Frame parsing → `wsEventBus` emission (the React layer never
 *     touches `MessageEvent`)
 *
 * Does NOT own:
 *   - React state (lives in `useWebSocket.ts`)
 *   - Auth-store subscription (the orchestrator in `useWebSocket`
 *     watches `useAuth` and calls `close()` + `connect()` on token
 *     change — keeps this class single-purpose and easy to test)
 *   - Visibility observation (also in `useWebSocket`)
 *
 * URL derivation: `VITE_GATEWAY_BASE_URL` with `http(s)` → `ws(s)` and
 * `/ws/mirlai/v1` appended. The default mirrors `gateway-client.ts`
 * (`http://localhost:8200` → `ws://localhost:8200/ws/mirlai/v1`).
 *
 * Per F-310 technical spec § 9 and task T-008.
 */
import { eventBus } from "./eventBus";
import { wsEventBus, type WSEventMap } from "./wsEventBus";

/** Default base URL — must stay in sync with `gateway-client.ts`. */
const DEFAULT_GATEWAY_BASE_URL = "http://localhost:8200";

/** Path appended to the gateway base URL to form the WS endpoint. */
const WS_PATH = "/ws/mirlai/v1";

/** Topics auto-subscribed on every (re)connect. */
const DEFAULT_TOPICS: ReadonlyArray<string> = ["devices", "suggestions"];

/**
 * Exponential backoff schedule (ms). Index by `reconnectAttempt`; the
 * last entry repeats indefinitely. Jitter is applied on top in
 * `scheduleReconnect`.
 *
 * Per task § 6 and technical spec risk register.
 */
const RECONNECT_DELAYS_MS: ReadonlyArray<number> = [1000, 2000, 4000, 8000, 16000, 30000];

/**
 * CloseEvent code mapping:
 *   - 1000 — normal closure (we initiated; do not reconnect)
 *   - 1008 — policy violation: F-300 says "your token is no longer
 *     accepted". This triggers `auth:reauth-required` on the auth bus
 *     so the router-level handler can navigate to re-auth. We do NOT
 *     reconnect — there's no point until the user re-authenticates.
 */
const CLOSE_CODE_NORMAL = 1000;
const CLOSE_CODE_POLICY_VIOLATION = 1008;

/**
 * Callbacks the React layer (`useWebSocket`) hands to the connection
 * wrapper. The wrapper invokes these on every state transition so the
 * Zustand store can mirror status without re-listening on the bus.
 *
 * All handlers are optional — they're observer hooks, not part of the
 * core message contract. The bus is the canonical place to receive
 * domain events; these are for connection-level state.
 */
export interface WSHandlers {
  /** Fired the moment the underlying `WebSocket` opens (pre-auth). */
  onConnecting?: () => void;
  /** Fired after the server returns `auth_ok` — connection is fully usable. */
  onOpen?: () => void;
  /** Fired on any close (server, client, network). */
  onClose?: (ev: CloseEvent) => void;
  /** Fired on transport errors. Note: native `WebSocket` errors are intentionally opaque. */
  onError?: (msg: string) => void;
  /** Fired right before `scheduleReconnect` arms the timer — exposes the next delay. */
  onReconnectScheduled?: (delayMs: number, attempt: number) => void;
}

/**
 * Construct the WS URL from a base HTTP(S) URL. Falls back to the
 * documented dev default when the input is empty/null. Strips a
 * trailing slash on the base so the appended path doesn't double up.
 *
 * Exported for tests + the `useWebSocket` store's logging.
 */
export function buildWebSocketUrl(baseUrl: string | null | undefined): string {
  const raw = (baseUrl && baseUrl.length > 0 ? baseUrl : DEFAULT_GATEWAY_BASE_URL).replace(
    /\/+$/,
    "",
  );
  // Swap protocol: http → ws, https → wss. Anything else passes through
  // unchanged (e.g. `ws://` already correct).
  const swapped = raw.replace(/^http:/i, "ws:").replace(/^https:/i, "wss:");
  return `${swapped}${WS_PATH}`;
}

/**
 * Compute the next reconnect delay with ±20% jitter, per task § 6.
 * Exported for tests so the jitter range can be asserted directly.
 */
export function computeReconnectDelay(attempt: number, rand: () => number = Math.random): number {
  const idx = Math.min(Math.max(attempt, 0), RECONNECT_DELAYS_MS.length - 1);
  const base = RECONNECT_DELAYS_MS[idx] ?? RECONNECT_DELAYS_MS[RECONNECT_DELAYS_MS.length - 1] ?? 30000;
  const jitter = 0.8 + rand() * 0.4; // 80% – 120%
  return Math.round(base * jitter);
}

/**
 * Test seam — `useWebSocket` may need to suspend reconnect when the
 * tab is hidden. The wrapper offers a single boolean toggle rather
 * than a full event API so the surface area stays minimal.
 */
export interface MirlaiWebSocketOptions {
  /** Topics to subscribe to after `auth_ok`. Defaults to `["devices", "suggestions"]`. */
  topics?: ReadonlyArray<string>;
  /**
   * Override the global `WebSocket` constructor. Tests using
   * `jest-websocket-mock` (which polyfills `globalThis.WebSocket`) do
   * not need this; it exists for environments without a global WS.
   */
  WebSocketImpl?: typeof WebSocket;
  /**
   * Inject a random source for the jitter calculation — tests pass
   * `() => 0` for deterministic delays.
   */
  random?: () => number;
}

/**
 * Wraps a single F-300 WebSocket connection. One instance per app —
 * `useWebSocket` enforces the singleton.
 */
export class MirlaiWebSocket {
  private ws: WebSocket | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Set when `close()` is called explicitly — disables reconnect. */
  private explicitClose = false;
  /**
   * Set when the wrapper is paused by the visibility handler. Pending
   * reconnects are cancelled; on `resume()` we immediately retry once
   * so the user doesn't wait a full backoff after un-hiding the tab.
   */
  private paused = false;
  /** Whether the server has acknowledged our auth frame this session. */
  private authed = false;

  private readonly topics: ReadonlyArray<string>;
  private readonly WS: typeof WebSocket;
  private readonly random: () => number;

  constructor(
    private readonly url: string,
    private readonly getToken: () => string | null,
    private readonly handlers: WSHandlers = {},
    options: MirlaiWebSocketOptions = {},
  ) {
    this.topics = options.topics ?? DEFAULT_TOPICS;
    // `jest-websocket-mock` patches `globalThis.WebSocket`, so reading it
    // late (here, not at module-load time) lets tests substitute their
    // mock without needing the explicit `WebSocketImpl` option.
    const Impl = options.WebSocketImpl ?? (globalThis as { WebSocket: typeof WebSocket }).WebSocket;
    if (!Impl) {
      throw new Error("MirlaiWebSocket: no WebSocket implementation available in this environment");
    }
    this.WS = Impl;
    this.random = options.random ?? Math.random;
  }

  /**
   * Open the connection. Idempotent — if a socket is already open or
   * opening, this is a no-op. Clears any pending reconnect timer.
   *
   * Note: the constructor doesn't auto-connect so callers can wire
   * handlers + observe initial `disconnected` state before the first
   * transport attempt fires.
   */
  connect(): void {
    if (this.explicitClose) {
      // Re-arming after an explicit close — caller really does want a fresh start.
      this.explicitClose = false;
    }
    this.cancelReconnectTimer();
    if (this.ws && (this.ws.readyState === this.WS.OPEN || this.ws.readyState === this.WS.CONNECTING)) {
      return;
    }
    this.handlers.onConnecting?.();
    this.authed = false;
    try {
      this.ws = new this.WS(this.url);
    } catch (err) {
      // Constructor can throw if the URL is malformed; treat as a
      // close-with-error so the normal reconnect machinery runs.
      this.handlers.onError?.(err instanceof Error ? err.message : "WebSocket construction failed");
      this.scheduleReconnect();
      return;
    }
    this.ws.addEventListener("open", this.handleOpen);
    this.ws.addEventListener("message", this.handleMessage);
    this.ws.addEventListener("close", this.handleClose);
    this.ws.addEventListener("error", this.handleError);
  }

  /**
   * Explicitly close the connection. Cancels any pending reconnect
   * timer and prevents auto-reconnect from arming on the close event.
   * Use when the user logs out or the auth-store's JWT goes null.
   */
  close(): void {
    this.explicitClose = true;
    this.cancelReconnectTimer();
    if (this.ws) {
      // 1000 = normal closure; some servers reject custom codes from
      // the client side, so stick to the well-defined "normal" code.
      try {
        this.ws.close(CLOSE_CODE_NORMAL, "client-initiated");
      } catch {
        // Already closed / closing — ignore.
      }
    }
    this.cleanupSocket();
  }

  /**
   * Suspend reconnect attempts (used when the tab goes hidden — no
   * point burning battery / data on a screen the user can't see).
   * Existing open sockets stay open; pending reconnect timers are
   * cancelled.
   */
  pause(): void {
    this.paused = true;
    this.cancelReconnectTimer();
  }

  /**
   * Resume after `pause()`. If currently disconnected, fires an
   * immediate reconnect (jitter still applies, but the backoff timer
   * does not). The reconnect counter is preserved so a tab that
   * un-hides into a still-down server stays on the existing schedule.
   */
  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    if (this.explicitClose) return;
    if (this.ws && (this.ws.readyState === this.WS.OPEN || this.ws.readyState === this.WS.CONNECTING)) {
      return;
    }
    this.connect();
  }

  /**
   * Send a JSON-serializable message. No-op if the socket isn't open;
   * this is intentional — pre-auth `subscribe` calls are queued by the
   * auth-handshake flow, not by buffering at the transport.
   */
  send(msg: object): void {
    if (!this.ws || this.ws.readyState !== this.WS.OPEN) return;
    try {
      this.ws.send(JSON.stringify(msg));
    } catch (err) {
      this.handlers.onError?.(err instanceof Error ? err.message : "WebSocket send failed");
    }
  }

  /**
   * Re-send the subscribe frame for a new topic set (or the same set
   * after the server reconnects). Public for `useWebSocket.subscribe`.
   */
  subscribe(topics: ReadonlyArray<string>): void {
    if (!this.authed) return;
    this.send({ type: "subscribe", topics });
  }

  /** Inspect the underlying socket's `readyState`. Exposed for tests + the store's status mirror. */
  get readyState(): number {
    return this.ws?.readyState ?? this.WS.CLOSED;
  }

  // ---------------------------------------------------------------------
  // Internal handlers — bound as arrow methods so `removeEventListener`
  // sees the same reference and tear-down works cleanly.
  // ---------------------------------------------------------------------

  private handleOpen = (): void => {
    // NOTE: reconnect counter is reset on `auth_ok` (not here) — a
    // server that accepts the WS but rejects auth still warrants
    // backoff, otherwise we'd hammer it at 1 s intervals forever.
    const token = this.getToken();
    if (!token) {
      // No token at the moment of open — bail; the auth-store
      // subscription in `useWebSocket` will call `connect()` again once
      // a token lands. Close with normal code so we don't reconnect.
      this.explicitClose = true;
      this.ws?.close(CLOSE_CODE_NORMAL, "no-token");
      return;
    }
    this.send({ type: "auth", token });
    // Don't subscribe yet — wait for `auth_ok`. The server may also
    // proactively push a `hello` frame; we surface that on the bus but
    // do not gate subscribe on it (F-300's `hello` carries diagnostic
    // metadata, not auth confirmation).
  };

  private handleMessage = (ev: MessageEvent): void => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(typeof ev.data === "string" ? ev.data : String(ev.data));
    } catch {
      // Non-JSON / binary frame — F-300's protocol is JSON-only; drop it.
      return;
    }
    if (!parsed || typeof parsed !== "object") return;
    const frame = parsed as { type?: unknown };
    if (typeof frame.type !== "string") return;

    switch (frame.type) {
      case "auth_ok": {
        this.authed = true;
        // Successful handshake — reset the backoff schedule so the
        // next disconnect starts at 1 s again.
        this.reconnectAttempt = 0;
        wsEventBus.emit("auth_ok");
        this.handlers.onOpen?.();
        // Auto-subscribe on first auth_ok after open. Idempotent on
        // server side — re-subscribing after a resume just refreshes.
        this.send({ type: "subscribe", topics: [...this.topics] });
        return;
      }
      case "auth_error": {
        const reason = readString(frame, "reason") ?? "auth_error";
        wsEventBus.emit("auth_error", { reason });
        // Token is stale — flag the router-level handler. Close without
        // reconnecting; the auth store will trigger a fresh `connect()`
        // once a new token is provisioned.
        eventBus.emit("auth:reauth-required");
        this.explicitClose = true;
        this.ws?.close(CLOSE_CODE_NORMAL, "auth-error");
        return;
      }
      case "hello": {
        const sessionId = readString(frame, "session_id");
        if (sessionId) {
          wsEventBus.emit("hello", {
            session_id: sessionId,
            ha_version: readString(frame, "ha_version") ?? undefined,
            mirlai_version: readString(frame, "mirlai_version") ?? undefined,
          });
        }
        return;
      }
      // F-300 publishes `device_state_changed`; the task-spec name on
      // our bus is `device_state`. We accept both spellings so the
      // wire-name freeze is a one-line change later.
      case "device_state":
      case "device_state_changed": {
        const deviceId = readString(frame, "device_id");
        const newState = readObject(frame, "new_state") ?? readObject(frame, "state");
        if (deviceId) {
          wsEventBus.emit("device_state", {
            device_id: deviceId,
            new_state: newState ?? {},
          });
        }
        return;
      }
      case "suggestion_new":
      case "suggestion_created": {
        const suggestionId =
          readString(frame, "suggestion_id") ?? readNestedId(frame, "suggestion");
        if (suggestionId) {
          wsEventBus.emit("suggestion_new", { suggestion_id: suggestionId });
        }
        return;
      }
      case "suggestion_updated": {
        const suggestionId = readString(frame, "suggestion_id");
        if (suggestionId) {
          wsEventBus.emit("suggestion_updated", { suggestion_id: suggestionId });
        }
        return;
      }
      case "routine_triggered":
      case "routine_executed": {
        const routineId = readString(frame, "routine_id");
        if (routineId) {
          wsEventBus.emit("routine_triggered", { routine_id: routineId });
        }
        return;
      }
      case "ping": {
        // F-300 sends `ping` every 30 s; respond with `pong` so the
        // server doesn't drop us for non-responsiveness.
        this.send({ type: "pong" });
        return;
      }
      default:
        // Unknown frame — silently drop. The bus is closed-vocabulary;
        // adding noisy console.warn would spam dev consoles on every
        // future protocol additio.
        return;
    }
  };

  private handleClose = (ev: CloseEvent): void => {
    const wasExplicit = this.explicitClose;
    this.cleanupSocket();
    this.handlers.onClose?.(ev);

    if (ev.code === CLOSE_CODE_POLICY_VIOLATION) {
      // Server rejected us at the transport layer (e.g. JWT failed
      // server-side validation). Fire re-auth and stop reconnecting.
      eventBus.emit("auth:reauth-required");
      this.explicitClose = true;
      return;
    }

    if (wasExplicit) {
      // We initiated the close — don't reconnect.
      return;
    }

    if (this.paused) {
      // Tab hidden; wait for `resume()`.
      return;
    }

    this.scheduleReconnect();
  };

  private handleError = (): void => {
    // Native `WebSocket` errors are intentionally opaque (no detail
    // string), so surface a generic message. The `close` event always
    // follows and is where reconnect decisions are made.
    this.handlers.onError?.("websocket transport error");
  };

  private scheduleReconnect(): void {
    if (this.explicitClose || this.paused) return;
    const delay = computeReconnectDelay(this.reconnectAttempt, this.random);
    this.handlers.onReconnectScheduled?.(delay, this.reconnectAttempt);
    this.reconnectAttempt += 1;
    this.cancelReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private cancelReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private cleanupSocket(): void {
    if (!this.ws) return;
    this.ws.removeEventListener("open", this.handleOpen);
    this.ws.removeEventListener("message", this.handleMessage);
    this.ws.removeEventListener("close", this.handleClose);
    this.ws.removeEventListener("error", this.handleError);
    this.ws = null;
    this.authed = false;
  }
}

// -----------------------------------------------------------------------
// Small helpers for safe frame-field extraction. F-300's protocol is
// JSON; we treat every field as optional + type-check defensively so a
// malformed server frame doesn't crash the client.
// -----------------------------------------------------------------------

function readString(obj: object, key: string): string | null {
  const value = (obj as Record<string, unknown>)[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

function readObject(obj: object, key: string): Record<string, unknown> | null {
  const value = (obj as Record<string, unknown>)[key];
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Pull `id` from a nested object on a frame — used by F-300's older
 * `suggestion_created` shape `{ suggestion: { id, ... } }`.
 */
function readNestedId(obj: object, key: string): string | null {
  const nested = readObject(obj, key);
  if (!nested) return null;
  const id = nested["id"];
  return typeof id === "string" && id.length > 0 ? id : null;
}

/**
 * Test-only helpers + constants — production code should not depend on
 * these.
 */
export const __internal = {
  DEFAULT_GATEWAY_BASE_URL,
  DEFAULT_TOPICS,
  RECONNECT_DELAYS_MS,
  WS_PATH,
  CLOSE_CODE_NORMAL,
  CLOSE_CODE_POLICY_VIOLATION,
  buildWebSocketUrl,
  computeReconnectDelay,
  // Re-exported here so tests can subscribe without a second import.
  wsEventBus,
} satisfies Record<string, unknown>;

// Re-export type so consumers don't need a second import for type-only access.
export type { WSEventMap };
